import { createSelector } from 'redux-orm';

import orm from '../orm';
import { pathSelector } from './path';
import { isLocalId } from '../utils/local-id';

export const currentModalSelector = ({ core: { currentModal } }) => currentModal;

export const currentUserIdSelector = ({ auth: { userId } }) => userId;

export const allUsersSelector = createSelector(orm, ({ User }) =>
  User.getOrderedUndeletedQuerySet().toRefArray(),
);

export const allUsersExceptCurrentSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, currentUserId) =>
    User.getOrderedUndeletedQuerySet()
      .exclude({
        id: currentUserId,
      })
      .toRefArray(),
);

export const currentUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel.ref;
  },
);

export const factoriesForCurrentUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel
      .getOrderedFactoryMembershipsQuerySet()
      .toModelArray()
      .map(({ factory: factoryModel }) => {
        let notificationsTotal = 0;
        factoryModel.sites.toModelArray().forEach((siteModel) => {
          siteModel.devices.toModelArray().forEach((deviceModel) => {
            notificationsTotal += deviceModel.getUnreadNotificationsQuerySet().count();
          });
        });

        const firstSite = factoryModel.sites.first();
        const firstSiteId = firstSite && firstSite.id;

        return {
          ...factoryModel.ref,
          notificationsTotal,
          firstSiteId,
        };
      });
  },
);

export const membershipsForCurrentFactorySelector = createSelector(
  orm,
  (state) => pathSelector(state).factoryId,
  (state) => currentUserIdSelector(state),
  ({ Factory }, id, currentUserId) => {
    if (!id) {
      return id;
    }

    const factoryModel = Factory.withId(id);

    if (!factoryModel) {
      return factoryModel;
    }

    return factoryModel
      .getOrderedMembershipsQuerySet()
      .toModelArray()
      .map((factoryMembershipModel) => ({
        ...factoryMembershipModel.ref,
        isPersisted: !isLocalId(factoryMembershipModel.id),
        user: {
          ...factoryMembershipModel.user.ref,
          isCurrent: factoryMembershipModel.user.id === currentUserId,
        },
      }));
  },
);

export const devicesForCurrentUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel
      .getOrderedDevicesQuerySet()
      .toRefArray()
      .map(({ devices: deviceModel }) => {
        return {
          ...deviceModel,
        };
      });
  },
);

export const factoriesToLinesForCurrentUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel
      .getOrderedFactoryMembershipsQuerySet()
      .toModelArray()
      .map(({ factory: factoryModel }) => ({
        ...factoryModel.ref,
        sites: factoryModel
          .getOrderedSitesQuerySet()
          .toModelArray()
          .map((siteModel) => ({
            ...siteModel.ref,
            lines: siteModel.getOrderedLinesQuerySet().toRefArray(),
          })),
      }));
  },
);

export const notificationsForCurrentUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }

    return userModel
      .getOrderedUnreadNotificationsQuerySet()
      .toModelArray()
      .map((notificationModel) => ({
        ...notificationModel.ref,
        action: notificationModel.action && {
          ...notificationModel.action.ref,
          user: notificationModel.action.user.ref,
        },
        device: notificationModel.device && notificationModel.device.ref,
      }));
  },
);

export const lineForCurrentSiteSelector = createSelector(
  orm,
  (state) => pathSelector(state).siteId,
  ({ Site }, id) => {
    if (!id) {
      return id;
    }

    const siteModel = Site.withId(id);

    if (!siteModel) {
      return siteModel;
    }

    return siteModel
      .getOrderedLinesQuerySet()
      .toRefArray()
      .map((line) => line);
  },
);

export const actionsForCurrentDeviceSelector = createSelector(
  orm,
  (state) => pathSelector(state).deviceId,
  (state) => currentUserIdSelector(state),
  ({ Device }, id, currentUserId) => {
    if (!id) {
      return id;
    }

    const deviceModel = Device.withId(id);

    if (!deviceModel) {
      return deviceModel;
    }

    return deviceModel
      .getOrderedInDeviceActionsQuerySet()
      .toModelArray()
      .map((actionModel) => ({
        ...actionModel.ref,
        isPersisted: !isLocalId(actionModel.id),
        user: {
          ...actionModel.user.ref,
          isCurrent: actionModel.user.id === currentUserId,
        },
      }));
  },
);

export const isAdminSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }
    return ['superAdmin'].includes(userModel.role) || userModel.isAdmin;
  },
);

export const isManagerSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }
    return ['admin'].includes(userModel.role);
  },
);

export const isUserSelector = createSelector(
  orm,
  (state) => currentUserIdSelector(state),
  ({ User }, id) => {
    if (!id) {
      return id;
    }

    const userModel = User.withId(id);

    if (!userModel) {
      return userModel;
    }
    return ['user'].includes(userModel.role);
  },
);
